import { useState, Fragment } from 'react'
import { ChevronRight, ChevronDown, MapPin, Search, Store, Building2, TrendingDown, TrendingUp, Minus } from 'lucide-react'
import { cn, formatPrice, stripBrandPrefixFromProductName } from '@/lib/utils'
import type {
    DummyCompetitorBrandGroup, DummyCompetitorSKU,
} from '@/data/dummy-competitor-data'
import type { UserLocationData, UserPriceData } from '@/types'

// ============================================================================
// DATA UI PRIMITIVES (Strict, clean, low-noise)
// ============================================================================
const DATA_TABLE_CONTAINER = "rounded-lg border border-border/40 bg-background overflow-hidden"
const DATA_HEADER_ROW = "bg-muted/30 border-b border-border/40"
const DATA_HEADER_CELL = "px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground select-none"
const DATA_ROW = "border-b border-border/20 hover:bg-muted/10 transition-colors"
const DATA_CELL = "px-3 py-2 text-sm text-foreground"
const NUM_CELL = cn(DATA_CELL, "text-right tabular-nums")

// ============================================================================
// TYPES & HELPERS (Logic remains unchanged)
// ============================================================================
interface CompetitorSKUsTableProps {
    productName: string; productBrand?: string; competitorBrands: DummyCompetitorBrandGroup[];
    onSelectSKU?: (sku: DummyCompetitorSKU) => void; userBrand?: string; userLocationData?: UserLocationData | null;
}

function formatUnit(quantity: number | string | null, unit: string | null, variantSize?: string): string {
    if (unit && unit.includes(' x ')) return unit
    if (quantity != null && unit) return `1 x ${quantity} ${unit}`
    if (unit) return `1 x ${unit}`
    if (variantSize && variantSize !== '1 Pack') return variantSize
    return '-'
}

function getUserPriceAtCity(data: UserLocationData | null | undefined, name: string): UserPriceData | null {
    const item = data?.cityPrices.find(c => c.cityName.toLowerCase().trim() === name.toLowerCase().trim())
    return item ? { price: item.price, mrp: item.mrp, discountPercent: item.discountPercent, inStock: item.inStock } : null
}
function getUserPriceAtCluster(data: UserLocationData | null | undefined, name: string): UserPriceData | null {
    if (!data) return null; for (const city of data.cityPrices) { const cluster = city.clusterPrices?.find(cl => cl.clusterName?.toLowerCase().trim() === name.toLowerCase().trim()); if (cluster) return { price: cluster.price, mrp: cluster.mrp, discountPercent: cluster.discountPercent, inStock: cluster.inStock } } return null
}
function getUserPriceAtPincode(data: UserLocationData | null | undefined, pin: string): UserPriceData | null {
    if (!data) return null; for (const city of data.cityPrices) { for (const cl of city.clusterPrices || []) { const p = cl.pincodePrices?.find(p => p.pincode === pin); if (p) return { price: p.price, mrp: p.mrp, discountPercent: p.discountPercent, inStock: p.inStock } } const stores = city.storePrices?.filter(s => s.pincode === pin); if (stores && stores.length > 0) { const prices = stores.filter(s => s.price != null).map(s => s.price!); const avgPrice = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : null; const mrps = stores.filter(s => s.mrp != null).map(s => s.mrp!); const avgMrp = mrps.length > 0 ? mrps.reduce((a, b) => a + b, 0) / mrps.length : null; return { price: avgPrice, mrp: avgMrp, discountPercent: avgPrice != null && avgMrp != null && avgMrp > 0 ? Math.round(((avgMrp - avgPrice) / avgMrp) * 100) : null, inStock: stores.some(s => s.inStock) } } } return null
}
function getUserPriceAtStore(data: UserLocationData | null | undefined, name: string): UserPriceData | null {
    if (!data) return null; for (const city of data.cityPrices) { const store = city.storePrices?.find(s => s.storeName.toLowerCase().trim() === name.toLowerCase().trim()); if (store) return { price: store.price, mrp: store.mrp, discountPercent: store.discountPercent, inStock: store.inStock }; for (const cl of city.clusterPrices || []) { for (const pin of cl.pincodePrices || []) { const ns = pin.storePrices?.find(s => s.storeName.toLowerCase().trim() === name.toLowerCase().trim()); if (ns) return { price: ns.price, mrp: ns.mrp, discountPercent: ns.discountPercent, inStock: ns.inStock } } } } return null
}

function calculatePriceDiff(user: number | null, comp: number | null): { amount: number | null; percent: number | null; status: 'cheaper' | 'same' | 'expensive' | 'unknown' } {
    if (user === null || comp === null || comp === 0) return { amount: null, percent: null, status: 'unknown' }
    const amount = user - comp; const percent = (amount / comp) * 100
    let status: 'cheaper' | 'same' | 'expensive' = 'same'
    if (percent > 1) status = 'expensive'; else if (percent < -1) status = 'cheaper'
    return { amount, percent, status }
}

// ============================================================================
// DENSE DATA COMPONENTS
// ============================================================================

/** Combines Price, MRP, Discount, and Stock into a tight, highly readable block */
function MetricPriceCell({ data, isUser = false }: { data: UserPriceData | null; isUser?: boolean }) {
    if (!data || data.price == null) return <div className={cn(NUM_CELL, isUser && "bg-primary/[0.02]", "text-muted-foreground/50")}>-</div>
    
    return (
        <div className={cn(NUM_CELL, "flex flex-col items-end justify-center py-1.5", isUser && "bg-primary/[0.02] border-x border-primary/10")}>
            <div className="flex items-center gap-1.5">
                <span className="font-semibold text-foreground">₹{formatPrice(data.price)}</span>
                {/* Minimal stock indicator dot */}
                <div className={cn("w-1.5 h-1.5 rounded-full", data.inStock ? "bg-emerald-500" : "bg-rose-500")} title={data.inStock ? "In Stock" : "Out of Stock"} />
            </div>
            {(data.mrp != null && data.mrp > data.price) && (
                <div className="flex items-center gap-1.5 text-[10px] mt-0.5">
                    <span className="text-muted-foreground line-through opacity-70">₹{formatPrice(data.mrp)}</span>
                    <span className="text-primary/80 font-medium">{Math.round(data.discountPercent || 0)}%</span>
                </div>
            )}
        </div>
    )
}

/** Pure text difference indicator, colored for immediate semantic recognition */
function MetricDiffCell({ userPrice, compPrice, compInStock }: { userPrice: number | null; compPrice: number | null; compInStock: boolean }) {
    if (!compInStock) return <div className={cn(NUM_CELL, "text-xs font-medium text-muted-foreground")}>OOS</div>
    const diff = calculatePriceDiff(userPrice, compPrice)
    if (diff.status === 'unknown') return <div className={cn(NUM_CELL, "text-muted-foreground/50")}>-</div>

    const config = {
        cheaper: { color: 'text-emerald-600 dark:text-emerald-400', icon: TrendingDown, sign: '-' },
        same: { color: 'text-muted-foreground', icon: Minus, sign: '' },
        expensive: { color: 'text-rose-600 dark:text-rose-400', icon: TrendingUp, sign: '+' }
    }[diff.status]
    const Icon = config.icon

    if (diff.status === 'same') return <div className={cn(NUM_CELL, "text-xs text-muted-foreground flex items-center justify-end")}><Minus className="w-3 h-3" /></div>

    return (
        <div className={cn(NUM_CELL, "flex flex-col items-end justify-center py-1.5", config.color)}>
            <div className="flex items-center gap-1">
                <Icon className="w-3 h-3 opacity-80" />
                <span className="font-bold text-sm">₹{Math.abs(diff.amount!).toFixed(0)}</span>
            </div>
            <span className="text-[10px] font-medium opacity-80 mt-0.5">
                {config.sign}{Math.abs(diff.percent!).toFixed(1)}%
            </span>
        </div>
    )
}

// ============================================================================
// SKU TABLE ROWS (Flat, analytical layout)
// ============================================================================
function BrandRow({ brand, isExpanded, onToggle, isUserBrand }: { brand: DummyCompetitorBrandGroup; isExpanded: boolean; onToggle: () => void; isUserBrand?: boolean }) {
    const avgMrp = brand.skus.length > 0 ? brand.skus.reduce((sum, sku) => sum + sku.mrp, 0) / brand.skus.length : 0
    return (
        <div className={cn(DATA_ROW, "cursor-pointer group", isUserBrand && "bg-primary/[0.02]")} onClick={onToggle}>
            <div className="grid grid-cols-[minmax(220px,1.5fr)_80px_70px_80px_80px_100px_60px] items-center">
                <div className={cn(DATA_CELL, "flex items-center gap-2")}>
                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                    <span className="font-semibold">{brand.brand}</span>
                    {isUserBrand && <span className="px-1.5 border border-primary/20 bg-primary/5 text-primary text-[9px] uppercase font-bold rounded">You</span>}
                    <span className="text-[10px] text-muted-foreground ml-2">({brand.skuCount})</span>
                </div>
                <div className={NUM_CELL}>-</div>
                <div className={NUM_CELL}>{brand.avgRating.toFixed(1)}</div>
                <div className={NUM_CELL}>{formatPrice(brand.avgDiscount)}%</div>
                <div className={cn(NUM_CELL, "text-muted-foreground line-through text-xs")}>₹{formatPrice(avgMrp)}</div>
                <div className={cn(NUM_CELL, "font-bold")}>₹{formatPrice(brand.avgSellingPrice)}</div>
                <div className={NUM_CELL}>-</div>
            </div>
        </div>
    )
}

function SKURow({ sku, brandName, isSelected, onSelect }: { sku: DummyCompetitorSKU; brandName: string; isSelected: boolean; onSelect: () => void }) {
    return (
        <div onClick={onSelect} className={cn(DATA_ROW, "cursor-pointer pl-6", isSelected ? "bg-primary/[0.04] shadow-[inset_2px_0_0_0_hsl(var(--primary))]" : "bg-muted/5")}>
            <div className="grid grid-cols-[minmax(196px,1.5fr)_80px_70px_80px_80px_100px_60px] items-center">
                <div className={cn(DATA_CELL, "text-xs truncate")} title={stripBrandPrefixFromProductName(sku.name, brandName)}>
                    {stripBrandPrefixFromProductName(sku.name, brandName)}
                </div>
                <div className={NUM_CELL}>{formatUnit(sku.quantity, sku.unit, sku.variantSize)}</div>
                <div className={NUM_CELL}>{sku.avgRating.toFixed(1)}</div>
                <div className={NUM_CELL}>{formatPrice(sku.discountPercent)}%</div>
                <div className={cn(NUM_CELL, "text-muted-foreground text-xs")}>₹{formatPrice(sku.mrp)}</div>
                <div className={cn(NUM_CELL, "font-semibold")}>₹{formatPrice(sku.sellingPrice)}</div>
                <div className={NUM_CELL}>{sku.avgRank || '-'}</div>
            </div>
        </div>
    )
}

// ============================================================================
// GEOSPATIAL TABLE ROWS (Indented tree view)
// ============================================================================
function GeoRow({ 
    level, title, subtitle, icon: Icon, isExpanded, onToggle, hasChildren, userPrice, compPrice, compInStock 
}: { 
    level: number; title: string; subtitle?: string; icon: any; isExpanded?: boolean; onToggle?: () => void; hasChildren?: boolean; userPrice: UserPriceData | null; compPrice: number; compInStock: boolean 
}) {
    // Indentation logic for tree hierarchy
    const paddingLeft = level === 0 ? 'pl-3' : level === 1 ? 'pl-8' : level === 2 ? 'pl-14' : 'pl-20'
    
    return (
        <div className={cn(DATA_ROW, hasChildren ? "cursor-pointer" : "")} onClick={hasChildren ? onToggle : undefined}>
            <div className="grid grid-cols-[minmax(250px,1fr)_160px_160px_100px] items-stretch min-h-[48px]">
                {/* Location Node */}
                <div className={cn(DATA_CELL, paddingLeft, "flex items-center gap-2 relative")}>
                    {/* Tree connector lines */}
                    {level > 0 && <div className="absolute left-[18px] top-0 bottom-0 w-px bg-border/40" />}
                    {level > 1 && <div className="absolute left-[42px] top-0 bottom-0 w-px bg-border/40" />}
                    {level > 2 && <div className="absolute left-[66px] top-0 bottom-0 w-px bg-border/40" />}

                    <div className="flex items-center gap-1.5 min-w-0">
                        {hasChildren ? (
                            isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        ) : (
                            <div className="w-3.5 shrink-0" /> // Spacer for alignment
                        )}
                        <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className={cn("truncate font-medium", level === 0 ? "text-sm" : "text-xs")}>{title}</span>
                        {subtitle && <span className="text-[10px] text-muted-foreground ml-1 shrink-0">{subtitle}</span>}
                    </div>
                </div>

                {/* Data Columns */}
                <MetricPriceCell data={userPrice} isUser />
                <MetricPriceCell data={{ price: compPrice, mrp: null, discountPercent: null, inStock: compInStock }} />
                <MetricDiffCell userPrice={userPrice?.price ?? null} compPrice={compPrice} compInStock={compInStock} />
            </div>
        </div>
    )
}

function LocationAvailabilitySection({ sku, searchQuery, onSearchChange, userLocationData }: { sku: DummyCompetitorSKU | null; searchQuery: string; onSearchChange: (query: string) => void; userLocationData?: UserLocationData | null }) {
    const [expandedCities, setExpandedCities] = useState<Set<string>>(new Set())
    const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set())
    const [expandedPincodes, setExpandedPincodes] = useState<Set<string>>(new Set())

    const toggleSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => setter(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })

    if (!sku) return (
        <div className="mt-6 py-12 text-center border border-dashed border-border/60 rounded-lg bg-muted/5">
            <p className="text-sm text-muted-foreground">Select a SKU from the table above to analyze geographic pricing variance.</p>
        </div>
    )

    const filteredLocations = sku.locationAvailability.filter(c => !searchQuery || c.cityName.toLowerCase().includes(searchQuery.toLowerCase()) || c.clusters.some(cl => cl.clusterName.toLowerCase().includes(searchQuery.toLowerCase()) || cl.pincodes?.some(p => p.pincode.includes(searchQuery) || p.darkstores?.some(ds => ds.darkstoreName.toLowerCase().includes(searchQuery.toLowerCase())))))

    return (
        <div className="mt-8 flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-foreground">Spatial Pricing Variance</h4>
                <div className="relative w-64">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input placeholder="Filter by city, pin, store..." value={searchQuery} onChange={(e) => onSearchChange(e.target.value)} className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary/50" />
                </div>
            </div>

            <div className={cn(DATA_TABLE_CONTAINER, "overflow-x-auto")}>
                <div className="min-w-[700px]">
                    {/* Header */}
                    <div className={cn(DATA_HEADER_ROW, "grid grid-cols-[minmax(250px,1fr)_160px_160px_100px]")}>
                        <div className={cn(DATA_HEADER_CELL, "text-left")}>Hierarchy Level</div>
                        <div className={cn(DATA_HEADER_CELL, "text-right bg-primary/5 border-x border-primary/10 text-primary")}>Your Price</div>
                        <div className={cn(DATA_HEADER_CELL, "text-right")}>Competitor</div>
                        <div className={cn(DATA_HEADER_CELL, "text-right")}>Variance</div>
                    </div>

                    {/* Body */}
                    {filteredLocations.length === 0 ? (
                        <div className="py-8 text-center text-xs text-muted-foreground">No matching nodes found.</div>
                    ) : (
                        filteredLocations.map((city) => (
                            <Fragment key={city.cityId}>
                                <GeoRow level={0} title={city.cityName} subtitle={`(${city.clusters.length} clusters)`} icon={MapPin} isExpanded={expandedCities.has(city.cityId)} onToggle={() => toggleSet(setExpandedCities, city.cityId)} hasChildren={city.clusters.length > 0} userPrice={getUserPriceAtCity(userLocationData, city.cityName)} compPrice={city.avgSellingPrice} compInStock={city.clusters.some(c => c.stockStatus === 'In Stock') || city.stockedPercentage > 0} />
                                
                                {expandedCities.has(city.cityId) && city.clusters.filter(cl => !searchQuery || cl.clusterName.toLowerCase().includes(searchQuery.toLowerCase()) || cl.pincodes?.some(p => p.pincode.includes(searchQuery) || p.darkstores?.some(ds => ds.darkstoreName.toLowerCase().includes(searchQuery.toLowerCase())))).map((cluster) => {
                                    const hasPins = (cluster.pincodes?.length ?? 0) > 0
                                    const cExp = expandedClusters.has(cluster.clusterId)
                                    return (
                                        <Fragment key={cluster.clusterId}>
                                            <GeoRow level={1} title={cluster.clusterName} subtitle={hasPins ? `(${cluster.pincodeCount})` : undefined} icon={Store} isExpanded={cExp} onToggle={() => toggleSet(setExpandedClusters, cluster.clusterId)} hasChildren={hasPins} userPrice={getUserPriceAtCluster(userLocationData, cluster.clusterName)} compPrice={cluster.sellingPrice} compInStock={cluster.stockStatus === 'In Stock' || cluster.stockStatus === 'Low Stock'} />
                                            
                                            {cExp && hasPins && cluster.pincodes!.filter(p => !searchQuery || p.pincode.includes(searchQuery) || p.darkstores?.some(ds => ds.darkstoreName.toLowerCase().includes(searchQuery.toLowerCase()))).map((pincode) => {
                                                const pKey = `${cluster.clusterId}-${pincode.pincode}`
                                                const hasDS = (pincode.darkstores?.length ?? 0) > 0
                                                const pExp = expandedPincodes.has(pKey)
                                                return (
                                                    <Fragment key={pKey}>
                                                        <GeoRow level={2} title={pincode.pincode} icon={MapPin} isExpanded={pExp} onToggle={() => toggleSet(setExpandedPincodes, pKey)} hasChildren={hasDS} userPrice={getUserPriceAtPincode(userLocationData, pincode.pincode)} compPrice={pincode.sellingPrice} compInStock={pincode.stockStatus === 'In Stock' || pincode.stockStatus === 'Low Stock'} />
                                                        
                                                        {pExp && hasDS && pincode.darkstores!.filter(ds => !searchQuery || ds.darkstoreName.toLowerCase().includes(searchQuery.toLowerCase())).map((ds) => (
                                                            <GeoRow key={ds.darkstoreId} level={3} title={ds.darkstoreName} icon={Building2} userPrice={getUserPriceAtStore(userLocationData, ds.darkstoreName)} compPrice={ds.sellingPrice} compInStock={ds.stockStatus === 'In Stock' || ds.stockStatus === 'Low Stock'} />
                                                        ))}
                                                    </Fragment>
                                                )
                                            })}
                                        </Fragment>
                                    )
                                })}
                            </Fragment>
                        ))
                    )}
                </div>
            </div>
        </div>
    )
}

// ============================================================================
// MAIN EXPORT
// ============================================================================
export function CompetitorSKUsTable({ productName, productBrand, competitorBrands, onSelectSKU, userBrand, userLocationData }: CompetitorSKUsTableProps) {
    const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set())
    const [selectedSKU, setSelectedSKU] = useState<DummyCompetitorSKU | null>(null)
    const [skuSearchQuery, setSkuSearchQuery] = useState('')
    const [locationSearchQuery, setLocationSearchQuery] = useState('')

    const filteredBrands = competitorBrands
        .filter(b => !skuSearchQuery || b.brand.toLowerCase().includes(skuSearchQuery.toLowerCase()) || b.skus.some(s => s.name.toLowerCase().includes(skuSearchQuery.toLowerCase())))
        .sort((a, b) => {
            const aU = userBrand && a.brand.toLowerCase() === userBrand.toLowerCase(), bU = userBrand && b.brand.toLowerCase() === userBrand.toLowerCase()
            return aU && !bU ? -1 : (!aU && bU ? 1 : 0)
        })

    const totalBrands = filteredBrands.length
    const totalSKUs = filteredBrands.reduce((s, b) => s + b.skuCount, 0)
    const avgPrice = totalBrands > 0 ? filteredBrands.reduce((s, b) => s + b.avgSellingPrice, 0) / totalBrands : 0

    return (
        <div className="flex flex-col gap-6 font-sans">
            {/* Minimalist KPI Row */}
            <div className="grid grid-cols-3 gap-4 pb-4 border-b border-border/40">
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider mb-1">Market Entities</span>
                    <span className="text-xl font-medium tabular-nums text-foreground">{totalBrands} <span className="text-sm text-muted-foreground font-normal">Brands</span> / {totalSKUs} <span className="text-sm text-muted-foreground font-normal">SKUs</span></span>
                </div>
                <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider mb-1">Avg Comp Price</span>
                    <span className="text-xl font-medium tabular-nums text-foreground">₹{formatPrice(avgPrice)}</span>
                </div>
                <div className="flex flex-col items-end text-right">
                    <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider mb-1">Target Product</span>
                    <span className="text-sm font-medium text-foreground truncate max-w-full">{stripBrandPrefixFromProductName(productName, productBrand)}</span>
                </div>
            </div>

            {/* SKU Data Table */}
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-foreground">Competitor SKU Matrix</h4>
                    <div className="relative w-64">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <input placeholder="Filter by brand or SKU..." value={skuSearchQuery} onChange={(e) => setSkuSearchQuery(e.target.value)} className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary/50" />
                    </div>
                </div>

                <div className={cn(DATA_TABLE_CONTAINER, "overflow-x-auto")}>
                    <div className="min-w-[800px]">
                        <div className={cn(DATA_HEADER_ROW, "grid grid-cols-[minmax(220px,1.5fr)_80px_70px_80px_80px_100px_60px]")}>
                            <div className={cn(DATA_HEADER_CELL, "text-left pl-4")}>Entity</div>
                            <div className={DATA_HEADER_CELL}>Size</div>
                            <div className={DATA_HEADER_CELL}>Rating</div>
                            <div className={DATA_HEADER_CELL}>Disc %</div>
                            <div className={DATA_HEADER_CELL}>MRP</div>
                            <div className={DATA_HEADER_CELL}>Price</div>
                            <div className={DATA_HEADER_CELL}>Rank</div>
                        </div>

                        <div className="flex flex-col">
                            {filteredBrands.length === 0 ? (
                                <div className="py-8 text-center text-xs text-muted-foreground">No data available matching filters.</div>
                            ) : (
                                filteredBrands.map((brand) => (
                                    <Fragment key={brand.brand}>
                                        <BrandRow brand={brand} isExpanded={expandedBrands.has(brand.brand)} onToggle={() => setExpandedBrands(p => { const n = new Set(p); n.has(brand.brand) ? n.delete(brand.brand) : n.add(brand.brand); return n })} isUserBrand={userBrand ? brand.brand.toLowerCase() === userBrand.toLowerCase() : false} />
                                        {expandedBrands.has(brand.brand) && brand.skus.filter(s => !skuSearchQuery || s.name.toLowerCase().includes(skuSearchQuery.toLowerCase())).map((sku) => (
                                            <SKURow key={sku.id} sku={sku} brandName={brand.brand} isSelected={selectedSKU?.id === sku.id} onSelect={() => { setSelectedSKU(sku); onSelectSKU?.(sku) }} />
                                        ))}
                                    </Fragment>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <LocationAvailabilitySection sku={selectedSKU} searchQuery={locationSearchQuery} onSearchChange={setLocationSearchQuery} userLocationData={userLocationData} />
        </div>
    )
}
